/**
 * WlOperatorFilter
 * ----------------------------------------------------------------------------
 * Filtro de operador White Label — visual idêntico ao CrmSellerFilter da página
 * "Todos os Negócios" (Popover + Command + SmartAvatar). Reutiliza o lookup
 * leve `useOperatorsLookup`, que chama a RPC `wl_list_operators`.
 *
 * Props:
 *   - value:    user_id selecionado ou null (Todos)
 *   - onChange: callback (null = todos)
 *   - className: classes adicionais p/ se encaixar na ListPageFiltersBar
 *
 * RELEASE: 2026-05-30 v1.0.0 - Versão inicial.
 */
import React, { useState } from "react";
import { Check, ChevronDown, Users } from "lucide-react";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  CommandSeparator,
} from "@/components/ui/command";
import { SmartAvatar } from "@/components/ui/smart-avatar";
import { Skeleton } from "@/components/ui/skeleton";
import { useOperatorsLookup } from "@/hooks/wl/useSupportSessionsLookups";

interface WlOperatorFilterProps {
  value: string | null;
  onChange: (operatorId: string | null) => void;
  className?: string;
  label?: string;
}

export function WlOperatorFilter({
  value,
  onChange,
  className,
  label = "Operadores",
}: WlOperatorFilterProps) {
  const [open, setOpen] = useState(false);
  const { data: operators = [], isLoading } = useOperatorsLookup();

  // Operador atualmente selecionado (para exibir nome no trigger)
  const selected = operators.find((o) => o.user_id === value);

  const handleSelect = (id: string | null) => {
    onChange(id);
    setOpen(false);
  };

  if (isLoading) {
    return <Skeleton className={cn("h-6 sm:h-8 w-[120px]", className)} />;
  }

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          size="sm"
          className={cn(
            // Mesmas dimensões dos demais filtros da ListPageFiltersBar
            "h-6 text-[11px] sm:h-8 sm:text-sm px-2 sm:px-3 gap-2 font-normal justify-between",
            className,
          )}
        >
          {selected ? (
            <span className="flex items-center gap-2 min-w-0">
              <SmartAvatar
                src={null}
                name={selected.display_name || selected.email || "?"}
                size="xs"
                use3DFallback
                className="h-4 w-4 shrink-0"
              />
              <span className="truncate">
                {selected.display_name || selected.email}
              </span>
            </span>
          ) : (
            <span className="flex items-center gap-1.5 min-w-0">
              <Users className="h-3 w-3 opacity-70 shrink-0" />
              <span className="truncate">{label}</span>
            </span>
          )}
          <ChevronDown className="h-2.5 w-2.5 sm:h-3 sm:w-3 opacity-50 shrink-0" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[280px] p-0" align="start">
        <Command>
          <CommandInput placeholder="Buscar operador..." />
          <CommandList>
            <CommandEmpty>Nenhum operador encontrado.</CommandEmpty>
            <CommandGroup>
              <CommandItem
                value="__all__"
                onSelect={() => handleSelect(null)}
                className="cursor-pointer"
              >
                <div
                  className={cn(
                    "mr-2 h-4 w-4 rounded-full border flex items-center justify-center",
                    value === null
                      ? "border-primary bg-primary"
                      : "border-muted",
                  )}
                >
                  {value === null && (
                    <Check className="h-2.5 w-2.5 text-primary-foreground" />
                  )}
                </div>
                <Users className="h-4 w-4 mr-2 opacity-50" />
                <span>Todos Operadores</span>
              </CommandItem>
            </CommandGroup>
            <CommandSeparator />
            <CommandGroup heading="Operadores">
              {operators.map((op) => {
                const display = op.display_name || op.email || op.user_id;
                return (
                  <CommandItem
                    key={op.user_id}
                    // Permite filtragem por nome OU email no CommandInput
                    value={`${op.display_name ?? ""} ${op.email ?? ""}`.trim()}
                    onSelect={() => handleSelect(op.user_id)}
                    className="cursor-pointer"
                  >
                    <div
                      className={cn(
                        "mr-2 h-4 w-4 rounded-full border flex items-center justify-center shrink-0",
                        value === op.user_id
                          ? "border-primary bg-primary"
                          : "border-muted",
                      )}
                    >
                      {value === op.user_id && (
                        <Check className="h-2.5 w-2.5 text-primary-foreground" />
                      )}
                    </div>
                    <SmartAvatar
                      src={null}
                      name={display}
                      size="sm"
                      use3DFallback
                      className="mr-2 shrink-0"
                    />
                    <div className="flex flex-col flex-1 min-w-0">
                      <span className="truncate">
                        {op.display_name || op.email || "—"}
                      </span>
                      {op.email && op.display_name && (
                        <span className="text-xs text-muted-foreground truncate">
                          {op.email}
                        </span>
                      )}
                    </div>
                  </CommandItem>
                );
              })}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
